import { memoryStorage, type StoragePort } from '@batiflow/client-core';

/**
 * Browser storage behind the client-core storage port: `local` (localStorage: preferences such as
 * locale, theme, last organisation) or `session` (sessionStorage, this tab only: the action
 * waiting for a step-up). Every call is wrapped in try/catch: the accessor can throw (private
 * windows, blocked site data) and a failure must never break the app. Never secrets.
 */
export function createBrowserStorage(kind: 'local' | 'session' = 'local'): StoragePort {
  const fallback = memoryStorage();
  const area = (): Storage | null => {
    try {
      return kind === 'local' ? window.localStorage : window.sessionStorage;
    } catch {
      return null;
    }
  };
  return {
    getItem(key) {
      try {
        return area()?.getItem(key) ?? fallback.getItem(key);
      } catch {
        return fallback.getItem(key);
      }
    },
    setItem(key, value) {
      fallback.setItem(key, value);
      try {
        area()?.setItem(key, value);
      } catch {
        // storage full or blocked: the in-memory copy keeps the value for this page view
      }
    },
    removeItem(key) {
      fallback.removeItem(key);
      try {
        area()?.removeItem(key);
      } catch {
        // ignore
      }
    },
  };
}
